import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Trash2 } from 'lucide-react';
import type { LookupDto } from '../types/lookup';

interface LookupDeleteButtonProps {
  /** The lookup item to delete */
  lookup: LookupDto;
  /** Called when deletion is confirmed */
  onDelete: () => Promise<void>;
  /** Whether the deletion is in progress */
  isDeleting?: boolean;
}

/**
 * Delete button with confirmation dialog for lookup items
 * Shows a soft-delete warning explaining the item will be deactivated
 */
export function LookupDeleteButton({
  lookup,
  onDelete,
  isDeleting = false,
}: LookupDeleteButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await onDelete();
      setIsOpen(false);
    } catch {
      // Error is handled by parent
    }
  };

  // Only show for active items
  if (!lookup.isActive) {
    return null;
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="text-destructive hover:text-destructive hover:bg-destructive/10"
        title="Deactivate item"
        onClick={() => setIsOpen(true)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate "{lookup.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate the lookup item. It will no longer appear in
              dropdown menus but existing records using this value will be
              preserved. You can reactivate it later by enabling "Show inactive"
              and editing the item.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deactivating...' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
